import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, CheckCircle2, Loader2, LogOut, MapPin, Search, ShieldCheck } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AppDownloadButton from '../components/AppDownloadButton';
import Seo from '../components/Seo';
import Footer from '../components/Footer';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { WORKER_WHATSAPP_LINK } from '../constants/externalLinks';

type CandidateProfile = {
  id: string;
  name?: string | null;
  city?: string | null;
  phone?: string | null;
  skills?: string[] | null;
  professions?: string[] | null;
  work_preference?: string | null;
};

type PortalJob = {
  id: string;
  role?: string | null;
  photo_url?: string | null;
  description?: string | null;
  salary_min?: number | null;
  salary_max?: number | null;
  currency?: string | null;
  job_type?: string | null;
  skill_category?: string | null;
  status?: string | null;
  created_at?: string | null;
  campaign?: {
    id?: string | null;
    company_name?: string | null;
    country?: string | null;
    status?: string | null;
    poster_urls?: string[] | null;
    recruiter?: {
      recruiter_id?: string | null;
      logo_url?: string | null;
      banner_url?: string | null;
      is_verified?: boolean | null;
    } | Array<{
      recruiter_id?: string | null;
      logo_url?: string | null;
      banner_url?: string | null;
      is_verified?: boolean | null;
    }> | null;
  } | Array<{
    id?: string | null;
    company_name?: string | null;
    country?: string | null;
    status?: string | null;
    poster_urls?: string[] | null;
    recruiter?: {
      recruiter_id?: string | null;
      logo_url?: string | null;
      banner_url?: string | null;
      is_verified?: boolean | null;
    } | Array<{
      recruiter_id?: string | null;
      logo_url?: string | null;
      banner_url?: string | null;
      is_verified?: boolean | null;
    }> | null;
  }> | null;
};

type PortalApplication = {
  id: string;
  job_id: string;
  stage?: string | null;
  created_at?: string | null;
  job?: PortalJob | PortalJob[] | null;
};

type PortalTab = 'matched' | 'india' | 'abroad' | 'applications';

function normalizeCampaign(job: PortalJob) {
  return Array.isArray(job.campaign) ? job.campaign[0] || null : job.campaign || null;
}

function normalizeRecruiter(job: PortalJob) {
  const campaign = normalizeCampaign(job);
  if (!campaign) return null;
  return Array.isArray(campaign.recruiter) ? campaign.recruiter[0] || null : campaign.recruiter || null;
}

function normalizeJob(applicationJob: PortalApplication['job']) {
  return Array.isArray(applicationJob) ? applicationJob[0] || null : applicationJob || null;
}

function isIndiaCountry(country?: string | null) {
  const normalized = String(country || '').trim().toLowerCase();
  return !normalized || normalized === 'india' || normalized === 'in' || normalized === 'bharat';
}

function tokenize(value: string) {
  return value
    .toLowerCase()
    .split(/[^a-z0-9]+/i)
    .map((item) => item.trim())
    .filter(Boolean);
}

function jobMatchScore(job: PortalJob, candidate: CandidateProfile | null) {
  if (!candidate) return 0;
  const roleTokens = tokenize([job.role, job.skill_category, job.job_type].filter(Boolean).join(' '));
  const userTokens = [
    ...(candidate.professions || []),
    ...(candidate.skills || []),
  ].flatMap((value) => tokenize(String(value)));

  if (!userTokens.length) return 0;
  return userTokens.filter((token) => roleTokens.some((jobToken) => jobToken.includes(token) || token.includes(jobToken))).length;
}

function formatSalary(job: PortalJob) {
  if (!job.salary_min && !job.salary_max) return null;
  const currency = job.currency || 'INR';
  if (job.salary_min && job.salary_max) return `${currency} ${job.salary_min}-${job.salary_max}`;
  return `${currency} ${job.salary_min || job.salary_max}`;
}

function getJobImage(job: PortalJob) {
  if (job.photo_url) return job.photo_url;
  const campaign = normalizeCampaign(job);
  const recruiter = normalizeRecruiter(job);
  const poster = Array.isArray(campaign?.poster_urls) ? campaign?.poster_urls?.[0] : null;
  return poster || recruiter?.banner_url || recruiter?.logo_url || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=1200&auto=format&fit=crop';
}

export default function WorkerPortalPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [candidate, setCandidate] = useState<CandidateProfile | null>(null);
  const [jobs, setJobs] = useState<PortalJob[]>([]);
  const [applications, setApplications] = useState<PortalApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [applyingJobId, setApplyingJobId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<PortalTab>('matched');

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const focusJobId = params.get('job');

  useEffect(() => {
    if (!user?.id) return;

    let mounted = true;

    const fetchPortalData = async () => {
      setLoading(true);
      setError('');

      try {
        const fallbackPhone = String(user.user_metadata?.phone || user.email?.replace(/@karya\.com$/i, '') || '').replace(/\D/g, '').slice(0, 10);
        let candidateResp = await supabase
          .from('candidates')
          .select('id, name, city, phone, skills, professions, work_preference')
          .eq('id', user.id)
          .maybeSingle();

        if (!candidateResp.data && fallbackPhone) {
          candidateResp = await supabase
            .from('candidates')
            .select('id, name, city, phone, skills, professions, work_preference')
            .eq('phone', fallbackPhone)
            .maybeSingle();
        }

        if (!candidateResp.data) {
          await supabase
            .from('candidates')
            .upsert({
              id: user.id,
              phone: fallbackPhone || null,
              name: String(user.user_metadata?.name || 'Jobisa Worker'),
              skills: [],
              professions: [],
              experience_years: 0,
            }, {
              onConflict: 'id',
              ignoreDuplicates: false,
            });

          candidateResp = await supabase
            .from('candidates')
            .select('id, name, city, phone, skills, professions, work_preference')
            .eq('id', user.id)
            .maybeSingle();
        }

        const [jobsResp, appsResp] = await Promise.all([
          supabase
            .from('jobs')
            .select(`
              id,
              role,
              photo_url,
              description,
              salary_min,
              salary_max,
              currency,
              job_type,
              skill_category,
              status,
              created_at,
              campaign:campaigns!inner (
                id,
                company_name,
                country,
                status,
                poster_urls,
                recruiter:recruiters (
                  recruiter_id:id,
                  logo_url,
                  banner_url,
                  is_verified
                )
              )
            `)
            .in('status', ['open', 'active'])
            .eq('campaign.status', 'active')
            .order('created_at', { ascending: false })
            .limit(36),
          supabase
            .from('applications')
            .select(`
              id,
              job_id,
              stage,
              created_at,
              job:jobs (
                id,
                role,
                photo_url,
                salary_min,
                salary_max,
                currency,
                job_type,
                skill_category,
                campaign:campaigns (
                  company_name,
                  country,
                  poster_urls
                )
              )
            `)
            .eq('candidate_id', user.id)
            .order('created_at', { ascending: false }),
        ]);

        if (candidateResp.error) throw candidateResp.error;
        if (jobsResp.error) throw jobsResp.error;
        if (appsResp.error) throw appsResp.error;

        if (!mounted) return;

        const candidateProfile = (candidateResp.data as CandidateProfile | null) || null;
        setCandidate(candidateProfile);
        setJobs((jobsResp.data as PortalJob[]) || []);
        setApplications((appsResp.data as PortalApplication[]) || []);

        const preference = String(candidateProfile?.work_preference || '').toLowerCase();
        if (preference === 'local') setActiveTab('india');
        else if (preference === 'abroad') setActiveTab('abroad');
      } catch (err: any) {
        if (mounted) setError(err?.message || 'Could not load worker jobs.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void fetchPortalData();

    return () => {
      mounted = false;
    };
  }, [user?.id]);

  const appliedJobIds = useMemo(() => new Set(applications.map((application) => application.job_id)), [applications]);

  const filteredJobs = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return [...jobs]
      .filter((job) => {
        const campaign = normalizeCampaign(job);
        if (activeTab === 'india') return isIndiaCountry(campaign?.country);
        if (activeTab === 'abroad') return !isIndiaCountry(campaign?.country);
        return activeTab !== 'applications';
      })
      .filter((job) => {
        if (!normalizedSearch) return true;
        const campaign = normalizeCampaign(job);
        return [
          job.role,
          job.skill_category,
          job.job_type,
          campaign?.company_name,
          campaign?.country,
        ].filter(Boolean).some((value) => String(value).toLowerCase().includes(normalizedSearch));
      })
      .sort((left, right) => {
        const scoreDelta = jobMatchScore(right, candidate) - jobMatchScore(left, candidate);
        if (scoreDelta !== 0) return scoreDelta;
        return new Date(right.created_at || 0).getTime() - new Date(left.created_at || 0).getTime();
      });
  }, [activeTab, candidate, jobs, search]);

  const handleApply = async (jobId: string) => {
    if (!user?.id || appliedJobIds.has(jobId)) return;

    setApplyingJobId(jobId);
    setError('');

    try {
      const { error: insertError } = await supabase
        .from('applications')
        .insert({
          job_id: jobId,
          candidate_id: user.id,
          stage: 'applied',
        });

      if (insertError) throw insertError;

      const { data: jobData } = await supabase
        .from('jobs')
        .select('role, campaign:campaigns(recruiter_id, company_name)')
        .eq('id', jobId)
        .single();

      if (jobData?.campaign) {
        const campaign = Array.isArray(jobData.campaign) ? jobData.campaign[0] : jobData.campaign;
        if (campaign?.recruiter_id) {
          await supabase.from('notifications').insert({
            user_id: campaign.recruiter_id,
            type: 'new_application',
            title: 'New Applicant!',
            body: `${candidate?.name || 'A candidate'} applied for ${jobData.role || 'your job posting'}`,
            data: JSON.stringify({ jobId, candidateId: user.id }),
          });
        }
      }

      const { data: refreshedApplications, error: appsError } = await supabase
        .from('applications')
        .select(`
          id,
          job_id,
          stage,
          created_at,
          job:jobs (
            id,
            role,
            salary_min,
            salary_max,
            currency,
            job_type,
            skill_category,
            campaign:campaigns (
              company_name,
              country,
              poster_urls
            )
          )
        `)
        .eq('candidate_id', user.id)
        .order('created_at', { ascending: false });

      if (appsError) throw appsError;
      setApplications((refreshedApplications as PortalApplication[]) || []);
      setActiveTab('applications');
    } catch (err: any) {
      setError(err?.message || 'Could not apply to this job.');
    } finally {
      setApplyingJobId(null);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/worker/login', { replace: true });
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#fffaf5] text-slate-900">
      <Seo
        title="Worker Jobs Portal | Jobisa"
        description="Browse live roles on Jobisa web, apply directly, and keep your worker profile synced across WhatsApp, app, and website."
        path="/worker/portal"
        noindex={true}
      />

      <div className="border-b border-orange-100 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-5 lg:px-10">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-orange-600">Jobisa Worker Web</p>
            <h1 className="mt-2 text-2xl font-black tracking-tight text-slate-950">
              {candidate?.name ? `Welcome, ${candidate.name}` : 'Worker jobs portal'}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {candidate?.city ? `${candidate.city} • ` : ''}Browse roles, apply directly, and track your progress here.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <AppDownloadButton
              href="/download/worker"
              className="rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-700 transition hover:border-orange-300 hover:bg-orange-100"
            >
              Open App
            </AppDownloadButton>
            <Link
              to="/worker"
              className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
            >
              Worker Site
            </Link>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              <LogOut size={15} />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
        {error ? (
          <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {!candidate && !loading ? (
          <div className="rounded-[1.75rem] border border-orange-100 bg-white p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-slate-950">Your worker profile is not ready yet</h2>
            <p className="mt-3 max-w-2xl text-base leading-8 text-slate-600">
              We found your login session, but not a complete worker profile yet. Continue on WhatsApp or the Jobisa app first, then come back here to browse and apply.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={WORKER_WHATSAPP_LINK} target="_blank" rel="noreferrer" className="rounded-full bg-emerald-600 px-5 py-3 text-sm font-semibold text-white">Open WhatsApp</a>
              <AppDownloadButton href="/download/worker" className="rounded-full bg-[#F97316] px-5 py-3 text-sm font-semibold text-white">Open Jobisa App</AppDownloadButton>
            </div>
          </div>
        ) : loading ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            <Loader2 className="animate-spin text-orange-600" size={34} />
          </div>
        ) : (
          <>
            <div className="mb-8 flex flex-col gap-4 rounded-[1.75rem] border border-orange-100 bg-white p-6 shadow-sm lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-3">
                {(['matched', 'india', 'abroad', 'applications'] as PortalTab[]).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`rounded-full px-4 py-2 text-sm font-semibold transition ${activeTab === tab ? 'bg-slate-950 text-white' : 'border border-slate-200 bg-white text-slate-700 hover:border-slate-300'}`}
                  >
                    {tab === 'matched' ? 'Best match' : tab === 'india' ? 'India jobs' : tab === 'abroad' ? 'Abroad jobs' : 'Applications'}
                  </button>
                ))}
              </div>

              {activeTab !== 'applications' ? (
                <div className="flex items-center gap-3 rounded-full border border-slate-200 bg-slate-50 px-4 py-2.5">
                  <Search size={16} className="text-slate-400" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search jobs, company, country..."
                    className="w-full bg-transparent text-sm text-slate-700 outline-none lg:w-72"
                  />
                </div>
              ) : null}
            </div>

            {activeTab === 'applications' ? (
              <div className="space-y-4">
                {applications.length > 0 ? applications.map((application) => {
                  const job = normalizeJob(application.job);
                  const campaign = job ? normalizeCampaign(job) : null;
                  return (
                    <article key={application.id} className="rounded-[1.5rem] border border-orange-100 bg-white p-6 shadow-sm">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-600">
                            {String(application.stage || 'applied').replace(/_/g, ' ')}
                          </p>
                          <h3 className="mt-2 text-xl font-bold text-slate-950">{job?.role || 'Open role'}</h3>
                          <p className="mt-1 text-sm text-slate-500">
                            {campaign?.company_name || 'Hiring company'} • {campaign?.country || 'India'}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          <CheckCircle2 size={16} className="text-emerald-600" />
                          Applied on {application.created_at ? new Date(application.created_at).toLocaleDateString() : 'recently'}
                        </div>
                      </div>
                    </article>
                  );
                }) : (
                  <div className="rounded-[1.5rem] border border-orange-100 bg-white p-8 text-slate-600 shadow-sm">
                    No applications yet. Pick a job and apply from the cards above.
                  </div>
                )}
              </div>
            ) : filteredJobs.length > 0 ? (
              <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {filteredJobs.map((job) => {
                  const campaign = normalizeCampaign(job);
                  const recruiter = normalizeRecruiter(job);
                  const salary = formatSalary(job);
                  const isApplied = appliedJobIds.has(job.id);
                  const title = job.role || 'Open role';
                  const isFocused = focusJobId === job.id;

                  return (
                    <article
                      key={job.id}
                      className={`overflow-hidden rounded-[1.75rem] border bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-[0_20px_45px_-24px_rgba(234,88,12,0.32)] ${isFocused ? 'border-orange-400 ring-2 ring-orange-200' : 'border-orange-100'}`}
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img src={getJobImage(job)} alt={title} className="h-full w-full object-cover" />
                        <div className="absolute inset-0 bg-black/25" />
                        <div className="absolute left-4 top-4 flex flex-wrap gap-2">
                          <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-slate-900">
                            {campaign?.country || 'India'}
                          </span>
                          {recruiter?.is_verified ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                              <ShieldCheck size={12} />
                              Verified
                            </span>
                          ) : null}
                        </div>
                        <div className="absolute bottom-4 left-4 right-4">
                          <p className="text-sm font-semibold text-white/90">{campaign?.company_name || 'Hiring company'}</p>
                          <h3 className="mt-1 text-2xl font-bold text-white">{title}</h3>
                        </div>
                      </div>

                      <div className="p-6">
                        <div className="flex flex-wrap gap-2">
                          {salary ? (
                            <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">{salary}</span>
                          ) : null}
                          {job.job_type ? (
                            <span className="rounded-full bg-orange-50 px-3 py-1 text-sm font-semibold text-orange-700">{job.job_type}</span>
                          ) : null}
                          {job.skill_category ? (
                            <span className="rounded-full bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-700">{job.skill_category}</span>
                          ) : null}
                        </div>

                        {job.description ? (
                          <p className="mt-4 line-clamp-3 text-sm leading-7 text-slate-600">{job.description}</p>
                        ) : null}

                        <div className="mt-5 flex items-center gap-2 text-sm text-slate-500">
                          <MapPin size={15} />
                          {campaign?.country || 'India'}
                        </div>

                        <div className="mt-6 flex gap-3">
                          <button
                            onClick={() => void handleApply(job.id)}
                            disabled={isApplied || applyingJobId === job.id}
                            className={`inline-flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-semibold transition ${isApplied ? 'bg-emerald-50 text-emerald-700' : 'bg-[#F97316] text-white hover:bg-[#EA580C]'} disabled:opacity-70`}
                          >
                            {applyingJobId === job.id ? <Loader2 className="animate-spin" size={15} /> : null}
                            {isApplied ? 'Applied' : 'Apply now'}
                          </button>
                          <AppDownloadButton
                            href="/download/worker"
                            className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
                          >
                            App
                            <ArrowRight size={15} />
                          </AppDownloadButton>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-[1.5rem] border border-orange-100 bg-white p-8 text-slate-600 shadow-sm">
                No jobs matched this view yet. Try another tab, change your search, or open the app for more roles.
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}
